import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useGeneralExams, useLeaderboard, useInviteFriend } from '../lib/api';
import { VerifiedBadge } from '../components/VerifiedBadge';

export function ExamLeaderboardPage() {
  const { examId } = useParams();
  const { data: examsData } = useGeneralExams();
  const { data: lb } = useLeaderboard('exam', examId ?? null);

  const exam = examsData?.exams?.find(e => e.id === examId);
  const entries = lb?.entries ?? [];
  const PAGE_SIZE = 20;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const visible = entries.slice(0, visibleCount);
  const hasMore = visibleCount < entries.length;

  return (
    <div className="max-w-lg mx-auto px-4 pb-20">
      {/* Header */}
      <div className="flex items-center gap-3 pt-2 mb-5">
        <Link to={`/exam/${examId}`} className="text-squishy-primary">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
            <path fillRule="evenodd" d="M17 10a.75.75 0 01-.75.75H5.612l4.158 3.96a.75.75 0 11-1.04 1.08l-5.5-5.25a.75.75 0 010-1.08l5.5-5.25a.75.75 0 111.04 1.08L5.612 9.25H16.25A.75.75 0 0117 10z" clipRule="evenodd" />
          </svg>
        </Link>
        <div>
          <h1 className="text-xl font-bold text-squishy-dark">Leaderboard</h1>
          <p className="text-xs text-slate-500">{exam?.code ?? ''} &middot; 30 day</p>
        </div>
      </div>

      {entries.length === 0 ? (
        <div className="text-center py-12 text-slate-400 text-sm">No activity yet &mdash; be the first!</div>
      ) : (
        <>
          <div className="bg-white border rounded-xl overflow-hidden">
            <ol className="divide-y">
              {visible.map(e => (
                <LeaderboardRow key={e.userId} entry={e} />
              ))}
            </ol>
          </div>

          {hasMore && (
            <button
              onClick={() => setVisibleCount(prev => prev + PAGE_SIZE)}
              className="w-full mt-4 py-3 bg-slate-100 hover:bg-slate-200 rounded-xl text-sm font-medium text-slate-600 transition-colors"
            >
              Show More ({entries.length - visibleCount} remaining)
            </button>
          )}

          {lb?.currentUserRank && (
            <p className="text-xs text-slate-500 mt-3 text-center">
              Your rank: #{lb.currentUserRank} of {lb.totalUsers}
            </p>
          )}
        </>
      )}
    </div>
  );
}

function LeaderboardRow({ entry: e }: { entry: any }) {
  const invite = useInviteFriend();
  const [showAdd, setShowAdd] = useState(false);

  return (
    <li className={`flex items-center gap-2.5 px-4 py-3 ${e.isCurrentUser ? 'bg-teal-50' : ''}`}>
      <span className="w-6 text-right text-xs font-mono text-slate-400">{e.rank}</span>
      <div className="w-8 h-8 rounded-full bg-slate-200 overflow-hidden flex items-center justify-center text-[11px] font-medium shrink-0">
        {e.avatarUrl ? <img src={e.avatarUrl} alt="" className="w-full h-full object-cover" /> : e.displayName[0]}
      </div>
      <button
        className="flex-1 text-left text-sm font-medium hover:text-squishy-primary transition inline-flex items-center gap-1 min-w-0"
        onClick={() => { if (!e.isCurrentUser) setShowAdd(!showAdd); }}
      >
        <span className="truncate">{e.displayName}</span>
        {e.verifiedStudent && <VerifiedBadge />}
      </button>
      {showAdd && !e.isCurrentUser && (
        <button
          onClick={async (ev) => {
            ev.stopPropagation();
            await invite.mutateAsync({ userId: e.userId });
            setShowAdd(false);
          }}
          disabled={invite.isPending}
          className="shrink-0 bg-squishy-primary text-white text-[10px] px-2.5 py-1 rounded-full disabled:opacity-50"
        >
          {invite.isSuccess ? 'Sent!' : invite.isPending ? '...' : '+ Friend'}
        </button>
      )}
      <span className="text-xs font-mono text-squishy-dark">{e.xp.toLocaleString()} XP</span>
    </li>
  );
}
